import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { specials } from "@/mocks/specials";

export default function SpecialDetail() {
  const { id } = useParams<{ id: string }>();
  const special = useMemo(() => specials.find((s) => s.id === id), [id]);
  const [openId, setOpenId] = useState<string | null>(null);

  usePageMeta({
    title: special ? `${special.title} - 生活中的玛丽` : "影评专题 - 生活中的玛丽",
    description: special
      ? `${special.title}专题：${special.intro.slice(0, 70)}…`
      : "生活中的玛丽影评专题，汇聚深度长评与影视解读。",
    keywords: special
      ? `生活中的玛丽,${special.title},影评专题,影视评论,${special.tags.join(",")}`
      : "生活中的玛丽,影评专题,影视评论",
  });

  const related = useMemo(() => specials.filter((s) => s.id !== id).slice(0, 2), [id]);

  if (!special) {
    return (
      <div className="w-full px-4 md:px-8 py-14">
        <div className="bg-background-50 rounded-lg border border-background-200">
          <EmptyState
            icon="ri-book-2-line"
            title="该专题不存在或已下线"
            description="你访问的影评专题可能已被移除，先去专题首页看看别的吧。"
          />
        </div>
        <div className="flex justify-center">
          <Link
            to="/special"
            className="mt-6 h-10 px-5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回专题首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <section className="relative w-full min-h-[320px] md:min-h-[420px] overflow-hidden bg-primary-950">
        <img
          src={special.cover}
          alt={`${special.title} 专题主图`}
          title={`${special.title} - 影评专题`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/92 via-primary-950/70 to-primary-950/50"></div>
        <div className="relative z-10 w-full px-4 md:px-8 py-8 md:py-14 min-h-[320px] md:min-h-[420px] flex flex-col justify-end">
          <div className="text-background-300">
            <Breadcrumb items={[{ label: "影评专题", to: "/special" }, { label: special.title }]} />
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {special.tags.map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
          <h1 className="mt-4 font-heading font-black text-2xl md:text-4xl text-background-50 max-w-3xl">
            {special.title}
          </h1>
          <p className="mt-4 text-sm md:text-base text-background-200 max-w-3xl leading-relaxed">
            {special.intro}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-4 text-xs md:text-sm text-background-300">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {special.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {special.date}
            </span>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-5">
            <div className="flex items-center gap-3">
              <span className="w-1 h-6 rounded-full bg-accent-500"></span>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">专题长评</h2>
            </div>

            {special.articles.map((article, idx) => {
              const open = openId === article.id;
              return (
                <article
                  key={article.id}
                  className="bg-background-50 rounded-lg border border-background-200 p-5 md:p-6"
                >
                  <div className="flex items-start gap-3">
                    <span className="shrink-0 font-heading font-black text-lg text-background-400">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div className="flex-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => setOpenId(open ? null : article.id)}
                        className="text-left cursor-pointer group"
                      >
                        <h3 className="font-heading font-bold text-base md:text-lg text-foreground-950 group-hover:text-primary-500 transition-colors">
                          {article.title}
                        </h3>
                      </button>
                      <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{article.summary}</p>

                      {open && (
                        <div className="mt-4 pt-4 border-t border-background-200 space-y-3 animate-fade-in-fast">
                          {article.content.map((p, i) => (
                            <p key={i} className="text-sm md:text-base text-foreground-700 leading-loose">
                              {p}
                            </p>
                          ))}
                        </div>
                      )}

                      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3 text-xs text-foreground-500">
                          <span className="flex items-center gap-1">
                            <i className="ri-user-line"></i>
                            {article.author}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-calendar-line"></i>
                            {article.date}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setOpenId(open ? null : article.id)}
                          className="text-sm text-secondary-600 hover:text-secondary-700 font-medium whitespace-nowrap flex items-center gap-1 cursor-pointer"
                        >
                          {open ? "收起全文" : "阅读全文"}
                          <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <aside className="lg:col-span-1 space-y-6">
            <div className="bg-background-50 rounded-lg border border-background-200 p-5">
              <h3 className="font-heading font-bold text-base text-foreground-950 mb-4">相关文章推荐</h3>
              <div className="space-y-4">
                {related.map((s) => (
                  <Link
                    key={s.id}
                    to={`/special/${s.id}`}
                    className="group flex gap-3 cursor-pointer"
                  >
                    <div className="w-20 h-14 rounded-md overflow-hidden bg-background-100 shrink-0">
                      <img
                        src={s.cover}
                        alt={`${s.title} 专题封面`}
                        title={`${s.title} - 影评专题`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {s.title}
                      </p>
                      <p className="mt-1 text-xs text-foreground-500">{s.articles.length} 篇长评</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="bg-primary-500 rounded-lg p-5">
              <h3 className="font-heading font-bold text-base text-background-50 mb-2">想继续逛？</h3>
              <p className="text-sm text-background-200 leading-relaxed">
                浏览更多专题，或直接从电影、电视剧分类找到感兴趣的作品。
              </p>
              <Link
                to="/special"
                className="mt-4 h-10 px-4 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium whitespace-nowrap flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
              >
                <i className="ri-book-open-line"></i>
                返回专题首页
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}